/**
 * AggregateChips — purple category chips (DM-26 / §12.4).
 *
 * Block M1a-FE (TZ 2026-07-29). Reuses useRemoveCategory. × → RemoveConfirmModal
 * (sibling) passing affected_row_count. После cascade — invalidate (уже в hook).
 *
 * Source of truth: `r.mapped_data.aggregate` is the dataset-wide bucket list
 * computed by BE `services/matching/aggregate.compute_aggregate` and re-written
 * by `merge_write_mapped_data` after every row. The SAME aggregate list is
 * mirrored on every DatasetRow.mapped_data (writer puts full state per row).
 * So we read `aggregate` from a single row (first one with mapped_data) —
 * summing across rows would N-times the counts and produce wrong chip labels.
 *
 * Path-label preference matches the BE rule: UA canonical unless language='ru'.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Tag } from 'lucide-react';

import {
  usePipelineMatchingRows,
  useRemoveCategory,
} from '@/hooks/useMatching';
import { cn } from '@/lib/utils';

export interface AggregateChipsProps {
  pipelineId: string | null;
  onPendingRemove: (chip: AggregateChipData | null) => void;
}

export interface AggregateChipData {
  key: string;
  label: string;
  row_count: number;
}

export function AggregateChips({
  pipelineId,
  onPendingRemove,
}: AggregateChipsProps) {
  const { t } = useTranslation();
  const { data: rowsResp } = usePipelineMatchingRows(pipelineId);
  const remove = useRemoveCategory();

  const chips = useMemo<AggregateChipData[]>(() => {
    // Pick the first row that has a non-null mapped_data. Per BE writer, every
    // row's `mapped_data.aggregate` is identical; reading once avoids N×counts.
    const firstWithMd = (rowsResp?.rows ?? []).find(
      (r) => r.mapped_data && typeof r.mapped_data === 'object',
    );
    const aggregate = (firstWithMd?.mapped_data as
      | {
          aggregate?: Array<{
            key: string;
            path_label?: string;
            row_count: number;
          }>;
          language?: 'ua' | 'ru';
        }
      | null | undefined)?.aggregate;
    if (!aggregate) return [];
    return aggregate
      .map<AggregateChipData>((a) => ({
        key: a.key,
        label: a.path_label ?? a.key,
        row_count: a.row_count,
      }))
      .sort((a, b) => b.row_count - a.row_count);
  }, [rowsResp]);

  if (!pipelineId || chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => (
        <div
          key={chip.key}
          className="flex items-center gap-2 bg-primary-500/15 border border-primary-500/30 rounded-full pl-3 pr-1 py-1 text-xs text-primary-200"
          data-testid="aggregate-chip"
        >
          <Tag className="w-3 h-3 text-primary-300" />
          <span>{chip.label}</span>
          <span className="text-primary-300/70">({chip.row_count})</span>
          <button
            type="button"
            onClick={() => onPendingRemove(chip)}
            disabled={remove.isPending}
            className={cn(
              'ml-1 p-1 rounded-full text-primary-300 hover:bg-danger-tint/30 hover:text-danger transition-colors',
              'disabled:opacity-50',
            )}
            title={t('process.matching.aggregate.removeTitle', {
              defaultValue: 'Remove category and reset rows',
            })}
            aria-label={t('process.matching.aggregate.removeTitle', {
              defaultValue: 'Remove category and reset rows',
            })}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
